import { useCallback } from 'react';
import confetti from 'canvas-confetti';

type ConfettiType = 'subtle' | 'celebration' | 'victory' | 'firstGame';

export function useConfetti() {
  const fire = useCallback((type: ConfettiType = 'celebration') => {
    const defaults = {
      zIndex: 10000,
      disableForReducedMotion: true,
    };

    switch (type) {
      case 'subtle':
        confetti({
          ...defaults,
          particleCount: 50,
          spread: 60,
          origin: { y: 0.6 },
          colors: ['#22d3ee', '#a855f7', '#22c55e'],
        });
        break;

      case 'celebration':
        // Burst from center
        confetti({
          ...defaults,
          particleCount: 100,
          spread: 70,
          origin: { y: 0.5 },
          colors: ['#22d3ee', '#a855f7', '#22c55e', '#f59e0b'],
        });
        // Side bursts
        setTimeout(() => {
          confetti({
            ...defaults,
            particleCount: 50,
            angle: 60,
            spread: 55,
            origin: { x: 0, y: 0.6 },
            colors: ['#22d3ee', '#a855f7'],
          });
          confetti({
            ...defaults,
            particleCount: 50,
            angle: 120,
            spread: 55,
            origin: { x: 1, y: 0.6 },
            colors: ['#22c55e', '#f59e0b'],
          });
        }, 200);
        break;

      case 'victory':
        // Firework-style from both sides
        const duration = 2000;
        const animationEnd = Date.now() + duration;

        const interval = setInterval(() => {
          const timeLeft = animationEnd - Date.now();
          if (timeLeft <= 0) {
            clearInterval(interval);
            return;
          }

          const particleCount = 50 * (timeLeft / duration);
          
          confetti({
            ...defaults,
            particleCount,
            angle: 60,
            spread: 55,
            origin: { x: 0, y: 0.7 },
            colors: ['#ffd700', '#ffaa00', '#22c55e'],
          });
          confetti({
            ...defaults,
            particleCount,
            angle: 120,
            spread: 55,
            origin: { x: 1, y: 0.7 },
            colors: ['#ffd700', '#ffaa00', '#22c55e'],
          });
        }, 250);
        break;

      case 'firstGame':
        // Extra special with shapes and emoji-like colors
        const count = 200;
        const defaultsFirstGame = {
          ...defaults,
          origin: { y: 0.7 },
        };

        function fireFirstGame(particleRatio: number, opts: confetti.Options) {
          confetti({
            ...defaultsFirstGame,
            ...opts,
            particleCount: Math.floor(count * particleRatio),
          });
        }

        fireFirstGame(0.25, {
          spread: 26,
          startVelocity: 55,
          colors: ['#ffd700'],
        });
        fireFirstGame(0.2, {
          spread: 60,
          colors: ['#22d3ee', '#a855f7'],
        });
        fireFirstGame(0.35, {
          spread: 100,
          decay: 0.91,
          scalar: 0.8,
          colors: ['#22c55e', '#f59e0b', '#ec4899'],
        });
        fireFirstGame(0.1, {
          spread: 120,
          startVelocity: 25,
          decay: 0.92,
          scalar: 1.2,
          colors: ['#ffd700', '#22d3ee'],
        });
        fireFirstGame(0.1, {
          spread: 120,
          startVelocity: 45,
          colors: ['#a855f7', '#22c55e'],
        });
        break;
    }
  }, []);

  return { fire };
}
